// Helpers for the company (broker) enrolment "fix issues" navigator. Validation
// still runs only at Proceed; this just walks the snapshot list it produced.

// Stepper "Page N" -> EnrolmentPageMapper view
const PAGE_TO_VIEW = {
  1: "STEP_TWO", // Plan (ProgramVariablesCompany)
  2: "STEP_THREE", // Corporate (CorporateFull)
  3: "STEP_FOUR", // Contacts
  4: "STEP_FIVE", // Banking (AddBankAccount)
};

// Which page/view owns an error. Primary signal is the "(Page N: ...)" tag in
// the message; falls back to the error key for the few without a tag.
export const mapErrorToView = (key, message) => {
  const pageMatch = /\(Page (\d+):/.exec(message || "");
  if (pageMatch) return PAGE_TO_VIEW[parseInt(pageMatch[1], 10)] || null;

  const k = (key || "").toLowerCase();
  if (
    k.indexOf("employee class") === 0 ||
    k.indexOf("year 1 end date") > -1 ||
    k.indexOf("effective termination") > -1 ||
    k.indexOf("claim processing fee") > -1 ||
    k.indexOf("action after") > -1
  )
    return "STEP_TWO";
  if (
    k.indexOf("bank") > -1 ||
    k.indexOf("authorized signer") > -1 ||
    k.indexOf("account number") > -1 ||
    k.indexOf("financial institution") > -1
  )
    return "STEP_FIVE";
  if (
    k.indexOf("government photo id") > -1 ||
    k.indexOf("date of birth") > -1 ||
    k.indexOf("first name") > -1 ||
    k.indexOf("last name") > -1 ||
    k.indexOf("director") > -1 ||
    k.indexOf("beneficial") > -1 ||
    k.indexOf("ownership") > -1
  )
    return "STEP_FOUR";
  if (
    k.indexOf("legal business name") > -1 ||
    k.indexOf("company name") > -1 ||
    k.indexOf("business contact occupation") > -1
  )
    return "STEP_THREE";
  return null;
};

// Map an error key to the field name / id anchor to scroll to. Returns null for
// controls that have no queryable target.
export const mapErrorToTarget = (key, message) => {
  if (!key) return null;

  // Email conflicts use a generic key ("Email Conflict N"); the field to change
  // is named in the message ("...Please change the <role> email..."). Parse it
  // so the navigator lands on the exact email input instead of the page top.
  if (/^Email Conflict/i.test(key)) {
    const msg = message || "";
    // The role to change is named either "change the <role> email" or
    // (when both are changeable) "use a different email for <role>".
    const rm =
      msg.match(/change the (.+?) email/i) ||
      msg.match(/use a different email for (.+?)(?:\s*\(|\.|$)/i);
    const role = rm ? rm[1].trim() : "";
    let em;
    if ((em = role.match(/^Authorized Signer (\d+)$/i)))
      return `authorizedSigners[${parseInt(em[1], 10) - 1}].email`;
    if (/^Plan Administrator$/i.test(role)) return "planAdminEmail";
    if ((em = role.match(/^Director (\d+)$/i)))
      return `directors[${parseInt(em[1], 10) - 1}].email`;
    if ((em = role.match(/^Beneficial Owner (\d+)$/i)))
      return `beneficiaryOwners[${parseInt(em[1], 10) - 1}].email`;
    return null;
  }

  // Any "Employee Class ..." error -> land on that class's section (id anchor).
  let m = key.match(/^Employee Class(?: (\d+))?/);
  if (m) {
    const i = m[1] ? parseInt(m[1], 10) - 1 : 0;
    return `err-employee-class-${i}`;
  }

  m = key.match(/^Authorized Signer \((\d+)\) : (.+)$/);
  if (m) {
    // Keys are 1-based ("... (1) ..."); FieldArray DOM indices are 0-based.
    const i = parseInt(m[1], 10) - 1;
    const map = {
      "first name": "firstName",
      "last name": "lastName",
      email: "email",
      occupation: "occupation",
      "contact number": "mobileNumber",
      address: "address",
      "date of birth": "dateOfBirth",
    };
    const f = map[m[2].toLowerCase().trim()];
    return f ? `authorizedSigners[${i}].${f}` : null;
  }

  // Plan Sponsor Directors (FieldArray "directors") - land on the specific
  // empty input, e.g. "Director Information (0) : First Name".
  m = key.match(/^Director(?: Information)? \((\d+)\) :\s+(.+)$/);
  if (m) {
    const i = parseInt(m[1], 10) - 1;
    const map = {
      "first name": "firstName",
      "last name": "lastName",
      email: "email",
      "contact number": "mobileNumber",
      occupation: "occupation",
      address: "address",
      "amount of ownership": "amountOfOwnership",
      "date of birth": "dateOfBirth",
    };
    const f = map[m[2].toLowerCase().trim()];
    return f ? `directors[${i}].${f}` : null;
  }

  // Beneficial Owners (FieldArray "beneficiaryOwners"). Two key prefixes are
  // used: "Beneficial Ownership Information (N)" and "Beneficial Owner (N)"
  // (the latter sometimes with a stray double space before the field name).
  m = key.match(/^Beneficial Owner(?:ship Information)? \((\d+)\) :\s+(.+)$/);
  if (m) {
    const i = parseInt(m[1], 10) - 1;
    const map = {
      "first name": "firstName",
      "last name": "lastName",
      email: "email",
      "amount of ownership": "amountOfOwnership",
      occupation: "occupation",
      address: "address",
      "date of birth": "dateOfBirth",
      "contact number": "mobileNumber",
    };
    const f = map[m[2].toLowerCase().trim()];
    return f ? `beneficiaryOwners[${i}].${f}` : null;
  }

  const simple = {
    "start date": "err-start-date",
    "end date": "err-end-date",
    "legal business name": "businessLegalName",
    "company name": "businessOperatedName",
    "business contact occupation": "businessContactOccupation",
    "first name": "firstName",
    "last name": "lastName",
    "government photo id number": "err-gov-id-number",
    "plan admin first name": "planAdminFirstName",
    "plan admin last name": "planAdminLastName",
    "plan admin email": "planAdminEmail",
    "plan admin contact number": "planAdminMobileNumber",
    "bank payment method": "opt-group",
    "bank branch number": "bankBranchNumber",
    "financial institution number": "bankNumber",
    "account number": "accountNumber",
    "effective termination date": "err-effective-termination-date",
    "year 1 end date": "err-year1-end-date",
    "date of birth": "err-date-of-birth",
    "government photo id expiry date": "err-id-expiry-date",
  };
  return simple[key.toLowerCase()] || null;
};

const isElementVisible = (el) =>
  !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));

const attemptScroll = (target, attempts, highlight) => {
  let el = null;
  if (target) {
    const variants = [target];
    if (target.indexOf("[") > -1) {
      variants.push(target.replace(/\[(\d+)\]/g, ".$1"));
    }
    for (const n of variants) {
      // The same anchor id/name can exist on more than one step at once (e.g.
      // the Review page AND the target step during a cross-page transition).
      // Gather every candidate and prefer a VISIBLE one, so we don't lock onto
      // a stale/unmounting copy and scroll nowhere (leaving the user at the top).
      const candidates = [
        ...document.getElementsByName(n),
        ...document.querySelectorAll(`[id="${n}"]`),
      ];
      let found = candidates.find(isElementVisible);
      // Some controls render zero-size (custom-styled radios, hidden native
      // inputs). Fall back to the nearest visible ancestor so the navigator
      // lands on the control's row instead of the top of the page.
      if (!found && candidates.length) {
        let a = candidates[0];
        while (a && !isElementVisible(a)) a = a.parentElement;
        found = a || candidates[0];
      }
      if (found) {
        el = found;
        break;
      }
    }
  }

  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Highlight only error fields — not navigation targets like the Proceed button.
    if (highlight) {
      try {
        el.focus({ preventScroll: true });
      } catch (e) {}
      const prevShadow = el.style.boxShadow;
      const prevTransition = el.style.transition;
      el.style.transition = "box-shadow 0.2s ease";
      el.style.boxShadow = "0 0 0 3px rgba(161, 49, 199, 0.7)";
      setTimeout(() => {
        el.style.boxShadow = prevShadow;
        el.style.transition = prevTransition;
      }, 2500);
    }
  } else if (attempts > 0) {
    setTimeout(() => attemptScroll(target, attempts - 1, highlight), 100);
  } else if (!target) {
    // No specific field to land on -> show the top of the page.
    window.scrollTo(0, 0);
  }
  // A specific target was requested but never found (e.g. the field isn't
  // rendered for this program type, or the issue snapshot is stale): leave the
  // scroll position as-is instead of yanking the user to the top of the page.
};

// Scroll to a target (field name or id anchor); flash-highlights it unless
// highlight=false (used for navigation targets like the Proceed button).
// The first lookup is deferred so the page we're leaving unmounts first — some
// anchors (e.g. the contribution-class section, rendered by the shared
// ClassFieldsRenderer) exist on BOTH the Review page and the target step, and
// we must not match the stale source copy.
export const scrollToErrorTarget = (target, highlight = true) => {
  setTimeout(() => attemptScroll(target, 30, highlight), 180);
};

// Build the ordered nav list from the validation-errors object.
export const buildErrorList = (errorsObj) =>
  Object.entries(errorsObj || {})
    .map(([key, message]) => ({
      key,
      message,
      view: mapErrorToView(key, message),
      target: mapErrorToTarget(key, message),
    }))
    .filter((e) => e.view);
